import { useEffect, useRef, useState, useCallback } from "react";
import MonacoEditor, { OnMount } from "@monaco-editor/react";
import type * as monacoTypes from "monaco-editor";
import { useParams } from "react-router-dom";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";

type MonacoMarkdownEditorProps = {
  className?: string;
  projectId: string;
};

export function MonacoMarkdownEditor({ className }: MonacoMarkdownEditorProps) {
  const { getMarkdown } = useEditorStore();
  const { getChapterContent } = useChapterStore();
  const chapterId = useParams().id || "";

  const editorRef = useRef<monacoTypes.editor.IStandaloneCodeEditor | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  // Get content from TipTap through editor store, fallback to chapter store
  const getTipTapContent = useCallback(() => {
    const tipTapContent = getMarkdown?.() || "";
    const chapterContent = getChapterContent(chapterId) || "";

    // Use TipTap content if available, otherwise fallback to chapter store
    const content = tipTapContent || chapterContent;
    return content;
  }, [getMarkdown, getChapterContent, chapterId]);

  // Initialize and continuously sync content from TipTap to Monaco
  useEffect(() => {
    if (!editorRef.current) return;

    const syncContent = () => {
      const tipTapContent = getTipTapContent();
      const monacoContent = editorRef.current?.getValue() || "";

      // Always sync on first load or when content is different
      if (!isInitialized || (tipTapContent !== monacoContent && !editorRef.current?.hasTextFocus())) {
        const position = editorRef.current?.getPosition();
        editorRef.current?.setValue(tipTapContent);

        if (!isInitialized) {
          setIsInitialized(true);
        }

        // Restore cursor position if user isn't actively editing
        if (position && !editorRef.current?.hasTextFocus()) {
          try {
            editorRef.current?.setPosition(position);
          } catch {
            // Position restoration failed, that's ok
          }
        }
      }
    };

    // Initial sync
    syncContent();

    // Set up periodic sync
    const interval = setInterval(syncContent, 200); // Check every 200ms

    return () => clearInterval(interval);
  }, [getTipTapContent, isInitialized]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setContainerWidth(width);
    });

    resizeObserver.observe(containerRef.current);
    setContainerWidth(containerRef.current.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  const onMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Enhanced markdown language configuration
    monaco.languages.setLanguageConfiguration("markdown", {
      wordPattern: /(-?\d*\.\d\w*)|([^`~!@#%^&*()\-=+[\]{}\\|;:'",./<>/?\\s]+)/g,
      brackets: [
        ["[", "]"],
        ["(", ")"],
        ["{", "}"],
        ["<", ">"]
      ],
      autoClosingPairs: [
        { open: "[", close: "]" },
        { open: "(", close: ")" },
        { open: "`", close: "`" },
        { open: '"', close: '"' },
        { open: "'", close: "'" }
      ],
      surroundingPairs: [
        { open: "[", close: "]" },
        { open: "(", close: ")" },
        { open: "`", close: "`" },
        { open: '"', close: '"' },
        { open: "'", close: "'" },
        { open: "*", close: "*" },
        { open: "_", close: "_" }
      ]
    });

    monaco.editor.defineTheme("markdownTheme", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "markup.heading.1.markdown", foreground: "1e40af", fontStyle: "bold" },
        { token: "markup.heading.2.markdown", foreground: "2563eb", fontStyle: "bold" },
        { token: "markup.heading.3.markdown", foreground: "3b82f6", fontStyle: "bold" },
        { token: "markup.heading.4.markdown", foreground: "60a5fa", fontStyle: "bold" },
        { token: "markup.heading.5.markdown", foreground: "93c5fd", fontStyle: "bold" },
        { token: "markup.heading.6.markdown", foreground: "bfdbfe", fontStyle: "bold" },
        { token: "markup.bold.markdown", foreground: "dc2626", fontStyle: "bold" },
        { token: "markup.italic.markdown", foreground: "7c3aed", fontStyle: "italic" },
        { token: "markup.inline.raw.markdown", foreground: "ea580c", background: "fef3c7" },
        { token: "markup.fenced_code.block.markdown", foreground: "ea580c", background: "f3f4f6" },
        { token: "markup.underline.link.markdown", foreground: "0891b2", fontStyle: "underline" },
        { token: "string.other.link.title.markdown", foreground: "0891b2" },
        { token: "string.other.link.description.markdown", foreground: "059669" },
        { token: "markup.list.markdown", foreground: "92400e" },
        { token: "markup.quote.markdown", foreground: "6b7280", fontStyle: "italic" },
        { token: "meta.tag.markdown", foreground: "e11d48", fontStyle: "bold" }
      ],
      colors: {
        "editor.background": "#ffffff",
        "editor.foreground": "#374151",
        "editorLineNumber.foreground": "#9ca3af",
        "editor.selectionBackground": "#dbeafe",
        "editor.lineHighlightBackground": "#f8fafc",
        "editorCursor.foreground": "#2563eb"
      }
    });

    monaco.languages.setMonarchTokensProvider("markdown", {
      tokenizer: {
        root: [
          [/^#{6}\s.*$/, "markup.heading.6.markdown"],
          [/^#{5}\s.*$/, "markup.heading.5.markdown"],
          [/^#{4}\s.*$/, "markup.heading.4.markdown"],
          [/^#{3}\s.*$/, "markup.heading.3.markdown"],
          [/^#{2}\s.*$/, "markup.heading.2.markdown"],
          [/^#{1}\s.*$/, "markup.heading.1.markdown"],
          [/\*\*[^*]+\*\*/, "markup.bold.markdown"],
          [/__[^_]+__/, "markup.bold.markdown"],
          [/\*[^*]+\*/, "markup.italic.markdown"],
          [/_[^_]+_/, "markup.italic.markdown"],
          [/```[\s\S]*?```/, "markup.fenced_code.block.markdown"],
          [/`[^`]+`/, "markup.inline.raw.markdown"],
          [/\[[^\]]+\]\([^)]+\)/, "markup.underline.link.markdown"],
          [/https?:\/\/[^\s]+/, "string.other.link.description.markdown"],
          [/^\s*[-*+]\s/, "markup.list.markdown"],
          [/^\s*\d+\.\s/, "markup.list.markdown"],
          [/^>\s.*$/, "markup.quote.markdown"],
          [/^[-*]{3,}$/, "markup.heading.1.markdown"],
          [/<[^>]+>/, "meta.tag.markdown"]
        ]
      }
    });

    monaco.editor.setTheme("markdownTheme");

    const formatTables = () => {
      const model = editor.getModel();
      if (!model) return;

      const content = model.getValue();
      const lines = content.split("\n");
      const formattedLines = [...lines];
      let hasChanges = false;

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (!line || !line.includes("|")) continue;

        let tableStart = i;
        let tableEnd = i;
        while (tableStart > 0) {
          const prevLine = lines[tableStart - 1].trim();
          if (!prevLine || !prevLine.includes("|")) break;
          tableStart--;
        }

        while (tableEnd < lines.length - 1) {
          const nextLine = lines[tableEnd + 1].trim();
          if (!nextLine || !nextLine.includes("|")) break;
          tableEnd++;
        }

        const tableRows = [];
        for (let j = tableStart; j <= tableEnd; j++) {
          const rowLine = lines[j].trim();
          if (rowLine.includes("|")) {
            const isSeparator = /^\|?[\s\-:]+(\|[\s\-:]+)*\|?$/.test(rowLine);
            let cells: string[];
            if (rowLine.startsWith("|") && rowLine.endsWith("|")) {
              cells = rowLine
                .slice(1, -1)
                .split("|")
                .map((cell) => cell.trim());
            } else if (rowLine.startsWith("|")) {
              cells = rowLine
                .slice(1)
                .split("|")
                .map((cell) => cell.trim());
            } else if (rowLine.endsWith("|")) {
              cells = rowLine
                .slice(0, -1)
                .split("|")
                .map((cell) => cell.trim());
            } else {
              cells = rowLine.split("|").map((cell) => cell.trim());
            }

            tableRows.push({
              index: j,
              cells,
              isSeparator,
              originalLine: lines[j]
            });
          }
        }

        if (tableRows.length > 1) {
          const maxColumns = Math.max(...tableRows.map((row) => row.cells.length));
          const columnWidths = new Array(maxColumns).fill(3);

          tableRows.forEach((row) => {
            if (!row.isSeparator) {
              row.cells.forEach((cell, colIndex) => {
                if (colIndex < maxColumns) {
                  columnWidths[colIndex] = Math.max(columnWidths[colIndex], cell.length + 2);
                }
              });
            }
          });

          for (const row of tableRows) {
            if (row.isSeparator) {
              let separator = "|";
              columnWidths.forEach((width) => {
                separator += "-".repeat(width) + "|";
              });
              formattedLines[row.index] = separator;
              hasChanges = true;
            } else {
              let formattedRow = "|";
              for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
                const cell = row.cells[colIndex] || "";
                const width = columnWidths[colIndex];
                const paddedCell = ` ${cell} `.padEnd(width);
                formattedRow += paddedCell.slice(0, width) + "|";
              }
              formattedLines[row.index] = formattedRow;
              hasChanges = true;
            }
          }

          i = tableEnd;
        }
      }

      if (hasChanges) {
        const formattedContent = formattedLines.join("\n");
        const position = editor.getPosition();
        const selection = editor.getSelection();

        editor.setValue(formattedContent);

        if (position) {
          try {
            editor.setPosition(position);
          } catch {
            // Ignore positioning errors
          }
        }
        if (selection) {
          try {
            editor.setSelection(selection);
          } catch {
            // Ignore selection errors
          }
        }
      }
    };

    editor.addAction({
      id: "format-tables",
      label: "Format Tables",
      keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyF],
      contextMenuGroupId: "modification",
      contextMenuOrder: 1.5,
      run: formatTables
    });
    editor.addCommand(monaco.KeyCode.Enter, () => {
      const position = editor.getPosition();
      if (position) {
        const model = editor.getModel();
        if (model) {
          const lineContent = model.getLineContent(position.lineNumber);

          if (lineContent.includes("|")) {
            editor.trigger("keyboard", "type", { text: "\n" });
            setTimeout(() => {
              if (!editor.hasTextFocus()) return;
              formatTables();
            }, 100);
            return;
          }
        }
      }

      editor.trigger("keyboard", "type", { text: "\n" });
    });
    const content = getTipTapContent();
    if (content) {
      editor.setValue(content);
      setIsInitialized(true);
    }

    // if (isEditable) {
    //   editor.focus();
    // }
  };

  const handleEditorChange = (_value: string | undefined) => {
    // const content = value || "";
    // if (isEditable) {
    //   setContent?.(content);
    // }
  };

  if (!chapterId) {
    return (
      <div className={`flex items-center justify-center h-full text-sm text-gray-500 ${className}`}>
        No chapter selected
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`h-full flex flex-col ${className}`}
      style={{ position: "relative", overflow: "hidden" }}
    >
      {/* Absolute positioning prevents Monaco from affecting container width */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
        <MonacoEditor
          key={chapterId} // Force re-mount when chapter changes
          height="100%"
          width={containerWidth > 0 ? `${containerWidth}px` : "100%"}
          language="markdown"
          theme="vs"
          defaultValue=""
          onMount={onMount}
          onChange={handleEditorChange}
          options={{
            readOnly: true,
            minimap: { enabled: false },
            wordWrap: "on",
            scrollBeyondLastLine: false,
            automaticLayout: true,
            fontSize: 15,
            lineNumbers: "on",
            renderWhitespace: "boundary",
            folding: true,
            lineHeight: 24,
            padding: { top: 20, bottom: 20 },
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              horizontalScrollbarSize: 8,
              verticalScrollbarSize: 8
            },
            bracketPairColorization: { enabled: true },
            renderLineHighlight: "line",
            tabSize: 2,
            insertSpaces: true,
            detectIndentation: true,
            hover: { enabled: true, delay: 500 },
            showFoldingControls: "always",
            smoothScrolling: true,
            mouseWheelZoom: true,
            contextmenu: true,
            glyphMargin: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 3,
            copyWithSyntaxHighlighting: true
          }}
        />
      </div>
    </div>
  );
}
